// Heritex platform catalogue — pillars × products
// status: 'live' (usable app)  |  'page' (marketing detail page exists) |  'soon'
// productSlug: matches /products/:slug if a detail page exists

export const PILLARS = [
  {
    slug: "sales",
    name: "Sales",
    icon: "TrendingUp",
    tagline: "Build pipeline. Close revenue.",
    summary:
      "Everything your sales team needs to capture leads, manage pipelines and close deals — without spreadsheets.",
    products: [
      { slug: "crm", name: "CRM", desc: "Pipelines & contacts that fit how your team works.", status: "live", route: "/app/crm", marketingSlug: "custom-crm" },
      { slug: "lead-forms", name: "Lead Capture Forms", desc: "Smart forms that auto-route to the right rep.", status: "soon" },
      { slug: "sales-sequences", name: "Sales Sequences", desc: "Multi-step email + WhatsApp outbound.", status: "soon" },
      { slug: "quotes", name: "Quote & Proposal Builder", desc: "Branded quotes with e-sign and payment.", status: "soon" },
      { slug: "forecasting", name: "Pipeline Forecasting", desc: "AI-assisted close-rate predictions.", status: "soon" },
      { slug: "partner-portal", name: "Partner / Channel Portal", desc: "Onboard, track and pay partners.", status: "soon" },
    ],
  },
  {
    slug: "customer-experience",
    name: "Customer Experience",
    icon: "Headset",
    tagline: "Support, retain, delight.",
    summary:
      "Turn first-time buyers into long-term advocates with help-desk, chat, AI and self-service in one calm stack.",
    products: [
      { slug: "help-desk", name: "Help Desk / Ticketing", desc: "Multi-channel inbox with SLAs.", status: "soon" },
      { slug: "live-chat", name: "Live Chat", desc: "Web widget with team routing.", status: "soon" },
      { slug: "ai-support-bot", name: "AI Support Bot", desc: "Doc-grounded answers, human handoff.", status: "page", marketingSlug: "ai-chatbots" },
      { slug: "knowledge-base", name: "Knowledge Base", desc: "Beautiful, searchable docs.", status: "soon" },
      { slug: "customer-portal", name: "Customer Portal", desc: "Self-service for invoices, tickets, files.", status: "soon" },
      { slug: "nps-reviews", name: "NPS & Reviews", desc: "Measure loyalty, capture testimonials.", status: "soon" },
    ],
  },
  {
    slug: "marketing",
    name: "Marketing",
    icon: "Megaphone",
    tagline: "Capture & nurture demand.",
    summary:
      "From the first ad click to the loyal repeat customer — landing pages, email, social, SEO and WhatsApp in one studio.",
    products: [
      { slug: "email-marketing", name: "Email Marketing & Automation", desc: "Drip campaigns + transactional.", status: "soon" },
      { slug: "landing-pages", name: "Landing Page Builder", desc: "Conversion-tuned templates.", status: "soon" },
      { slug: "seo-blog", name: "SEO Blog CMS", desc: "Editorial-grade publishing.", status: "page", marketingSlug: "business-websites" },
      { slug: "social-scheduler", name: "Social Scheduler", desc: "Plan, queue, post across channels.", status: "soon" },
      { slug: "whatsapp-drip", name: "WhatsApp Drip Campaigns", desc: "Behavioural sequences on WhatsApp.", status: "soon" },
      { slug: "webinars", name: "Webinar Funnels", desc: "Live + on-demand event funnels.", status: "soon" },
      { slug: "referrals", name: "Referral Program", desc: "Branded referrals with rewards.", status: "soon" },
    ],
  },
  {
    slug: "hr",
    name: "HR & People",
    icon: "Users",
    tagline: "Hire, onboard, grow.",
    summary:
      "Run the full employee lifecycle — recruiting through retention — without juggling five tools.",
    products: [
      { slug: "ats", name: "Applicant Tracking (ATS)", desc: "Pipelines for hiring, structured interviews.", status: "soon" },
      { slug: "onboarding", name: "Onboarding", desc: "Day-1 checklists, doc collection, provisioning.", status: "soon" },
      { slug: "directory", name: "Employee Directory", desc: "Org chart, profiles, self-service.", status: "soon" },
      { slug: "leave-attendance", name: "Leave & Attendance", desc: "Biometric / app, with approvals.", status: "soon" },
      { slug: "payroll", name: "Payroll", desc: "Salaries, PF / ESI, payslips, TDS.", status: "soon" },
      { slug: "performance", name: "Performance & 1:1s", desc: "OKRs, reviews, continuous feedback.", status: "soon" },
      { slug: "lnd", name: "L&D / Training Tracker", desc: "Courses, certifications, completion.", status: "soon" },
    ],
  },
  {
    slug: "operations",
    name: "Operations",
    icon: "GanttChart",
    tagline: "Run the business.",
    summary:
      "Projects, workflows, approvals and internal knowledge — the rails that keep the team moving.",
    products: [
      { slug: "projects", name: "Project Management", desc: "Boards, timelines, milestones.", status: "soon" },
      { slug: "tasks", name: "Task Boards", desc: "Lightweight kanban for any team.", status: "soon" },
      { slug: "wiki", name: "Internal Wiki", desc: "SOPs, runbooks, onboarding docs.", status: "soon" },
      { slug: "approvals", name: "Approvals", desc: "Multi-step approval flows.", status: "soon" },
      { slug: "vendors", name: "Vendor Management", desc: "POs, contracts, payments.", status: "soon" },
      { slug: "automation", name: "Workflow Automation", desc: "No-code, AI-augmented, 100+ connectors.", status: "page", marketingSlug: "automation" },
    ],
  },
  {
    slug: "finance",
    name: "Finance",
    icon: "Wallet",
    tagline: "Bill, collect, reconcile.",
    summary:
      "Invoicing, payments, subscriptions, expenses and accounting — GST-ready and Tally-compatible.",
    products: [
      { slug: "invoicing", name: "Invoicing", desc: "Branded invoices with payment links.", status: "soon" },
      { slug: "quotes-finance", name: "Quotes → Invoices", desc: "Quote, approve, invoice, reconcile.", status: "soon" },
      { slug: "expenses", name: "Expense Management", desc: "Receipts, approvals, reimbursements.", status: "soon" },
      { slug: "subscriptions", name: "Subscriptions / Recurring", desc: "Dunning, retries, pro-rations.", status: "soon" },
      { slug: "accounting", name: "Accounting (GST & Tally)", desc: "Books that talk to your CA.", status: "soon" },
      { slug: "cash-forecast", name: "Cash Flow Forecast", desc: "Runway and AR / AP visibility.", status: "soon" },
    ],
  },
  {
    slug: "commerce",
    name: "Commerce",
    icon: "ShoppingBag",
    tagline: "Sell anywhere.",
    summary:
      "Storefronts, marketplaces, restaurant ordering and POS — payments and ops glued in from day one.",
    products: [
      { slug: "storefront", name: "Storefront", desc: "Single-vendor D2C store.", status: "page", marketingSlug: "e-commerce" },
      { slug: "marketplace", name: "Multi-vendor Marketplace", desc: "Onboard sellers, take commission.", status: "soon" },
      { slug: "restaurant-ordering", name: "Restaurant Online Ordering", desc: "Menu, delivery, WhatsApp orders.", status: "soon" },
      { slug: "subscription-box", name: "Subscription Boxes", desc: "Recurring commerce.", status: "soon" },
      { slug: "b2b-wholesale", name: "B2B Wholesale Portal", desc: "Tiered prices, quote requests.", status: "soon" },
      { slug: "pos", name: "POS", desc: "Counter, kitchen and floor.", status: "soon" },
    ],
  },
  {
    slug: "ai",
    name: "AI & Automation",
    icon: "Bot",
    tagline: "Compounding leverage.",
    summary:
      "AI agents, voice, document extraction and no-code workflows — the layer that quietly does the boring work.",
    products: [
      { slug: "chatbots", name: "AI Chatbots", desc: "Trained on your data, on web + WhatsApp.", status: "page", marketingSlug: "ai-chatbots" },
      { slug: "voice-agents", name: "AI Voice Agents", desc: "Inbound / outbound voice agents.", status: "soon" },
      { slug: "no-code-workflows", name: "No-code Workflows", desc: "Trigger → conditions → actions.", status: "page", marketingSlug: "automation" },
      { slug: "doc-extraction", name: "Document Extraction", desc: "Invoices, IDs, receipts, contracts.", status: "soon" },
      { slug: "content-studio", name: "AI Content Studio", desc: "Brand-safe content at scale.", status: "soon" },
      { slug: "rag-builder", name: "Knowledge RAG Builder", desc: "Ground answers in your own data.", status: "soon" },
    ],
  },
];

export const CROSS_CUTTING = [
  {
    slug: "analytics",
    name: "Analytics",
    icon: "BarChart3",
    tagline: "Real-time insight, every team.",
    summary:
      "Plugs into every pillar — dashboards for sales, marketing, ops, finance and product, all in one view.",
    products: [
      { slug: "executive", name: "Executive Dashboard", desc: "MRR, growth, runway in one screen.", status: "page", marketingSlug: "analytics-dashboards" },
      { slug: "sales-analytics", name: "Sales Analytics", desc: "Pipeline, forecast, rep performance.", status: "soon" },
      { slug: "marketing-attribution", name: "Marketing Attribution", desc: "Channel ROI and funnel analysis.", status: "soon" },
      { slug: "ops-sla", name: "Operations Analytics", desc: "SLA, throughput, bottlenecks.", status: "soon" },
      { slug: "finance-analytics", name: "Finance Analytics", desc: "MRR, cohorts, ageing, runway.", status: "soon" },
    ],
  },
  {
    slug: "build",
    name: "Build",
    icon: "Wrench",
    tagline: "When off-the-shelf isn't enough.",
    summary:
      "Custom mobile apps, websites and software — designed and built by the same senior studio. Pricing as a service, not a SaaS.",
    products: [
      { slug: "mobile", name: "Custom Mobile Apps", desc: "iOS & Android, native-feel, owned by you.", status: "page", marketingSlug: "mobile-apps" },
      { slug: "websites", name: "Custom Websites", desc: "Editorial marketing sites that convert.", status: "page", marketingSlug: "business-websites" },
      { slug: "software", name: "Custom Software", desc: "Bespoke platforms when nothing fits.", status: "page", marketingSlug: "hospital-management" },
      { slug: "integrations", name: "API & Integrations", desc: "Build any connector you need.", status: "soon" },
    ],
  },
];

export const ALL_PILLARS = [...PILLARS, ...CROSS_CUTTING];
export const pillarBySlug = Object.fromEntries(ALL_PILLARS.map((p) => [p.slug, p]));
